import { Timestamp } from "firebase/firestore";
import {
  readDocument,
  createDocumentWithId,
  updateDocument,
} from "../firebase/db";
import { COLLECTIONS } from "../firebase/collectionNames";
import { UserCart, CartItem } from "../types/cart";
import { checkRateLimit, RATE_LIMITS } from "../utils/rateLimiter";

/**
 * Get user's cart document
 */
export async function getUserCart(userId: string): Promise<UserCart | null> {
  try {
    const cart = await readDocument<UserCart>(COLLECTIONS.userCart, userId);
    return cart;
  } catch (error) {
    console.error("Error fetching user cart:", error);
    return null;
  }
}

/**
 * Add or update item in user's cart
 * If mango already exists, increment quantity
 * If not, add new item
 */
export async function addToCart(
  userId: string,
  mangoId: string,
  quantity: number = 1
): Promise<void> {
  try {
    // Check rate limit for add to cart operations
    checkRateLimit(RATE_LIMITS.ADD_TO_CART);
    
    const existingCart = await getUserCart(userId);

    if (existingCart) {
      // Cart exists, check if mango already in cart
      const existingItemIndex = existingCart.items.findIndex(
        (item) => item.mangoId === mangoId
      );

      if (existingItemIndex >= 0) {
        // Mango exists, increment quantity
        const existingItem = existingCart.items[existingItemIndex];
        if (!existingItem) {
          throw new Error("Cart item not found");
        }
        const updatedItems = [...existingCart.items];
        updatedItems[existingItemIndex] = {
          mangoId: existingItem.mangoId,
          quantity: existingItem.quantity + quantity,
          addedAt: existingItem.addedAt,
        };

        await updateDocument<UserCart>(COLLECTIONS.userCart, userId, {
          items: updatedItems,
        });
      } else {
        // Mango doesn't exist, add new item
        const newItem: CartItem = {
          mangoId,
          quantity,
          addedAt: Timestamp.now(),
        };

        await updateDocument<UserCart>(COLLECTIONS.userCart, userId, {
          items: [...existingCart.items, newItem],
        });
      }
    } else {
      // Cart doesn't exist, create new cart
      const newCart: UserCart = {
        userId,
        items: [
          {
            mangoId,
            quantity,
            addedAt: Timestamp.now(),
          },
        ],
      };

      await createDocumentWithId<UserCart>(
        COLLECTIONS.userCart,
        userId,
        newCart
      );
    }
  } catch (error) {
    console.error("Error adding to cart:", error);
    throw error;
  }
}

/**
 * Update quantity of a specific item in cart
 */
export async function updateCartItemQuantity(
  userId: string,
  mangoId: string,
  quantity: number
): Promise<void> {
  try {
    // Check rate limit for cart operations
    checkRateLimit(RATE_LIMITS.CART_OPERATION);
    
    const existingCart = await getUserCart(userId);

    if (!existingCart) {
      throw new Error("Cart not found");
    }

    const updatedItems = existingCart.items.map((item) =>
      item.mangoId === mangoId ? { ...item, quantity } : item
    );

    await updateDocument<UserCart>(COLLECTIONS.userCart, userId, {
      items: updatedItems,
    });
  } catch (error) {
    console.error("Error updating cart item quantity:", error);
    throw error;
  }
}

/**
 * Remove item from cart
 */
export async function removeFromCart(
  userId: string,
  mangoId: string
): Promise<void> {
  try {
    // Check rate limit for cart operations
    checkRateLimit(RATE_LIMITS.CART_OPERATION);
    
    const existingCart = await getUserCart(userId);

    if (!existingCart) {
      throw new Error("Cart not found");
    }

    const updatedItems = existingCart.items.filter(
      (item) => item.mangoId !== mangoId
    );

    await updateDocument<UserCart>(COLLECTIONS.userCart, userId, {
      items: updatedItems,
    });
  } catch (error) {
    console.error("Error removing from cart:", error);
    throw error;
  }
}

/**
 * Clear all items from user's cart
 */
export async function clearCart(userId: string): Promise<void> {
  try {
    // Check rate limit for cart operations
    checkRateLimit(RATE_LIMITS.CART_OPERATION);
    
    const existingCart = await getUserCart(userId);

    if (!existingCart) {
      return; // Cart doesn't exist, nothing to clear
    }

    // Set items to empty array (better practice than deleting document)
    await updateDocument<UserCart>(COLLECTIONS.userCart, userId, {
      items: [],
    });
  } catch (error) {
    console.error("Error clearing cart:", error);
    throw error;
  }
}

